import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ArrowLeft, Lock, Send, User, Mail, Phone, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResearchPage() {
    const { id } = useParams();
    const [research, setResearch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
    const [submitting, setSubmitting] = useState(false);
    const [requested, setRequested] = useState(false);

    const fetchResearch = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/research/${id}`);
            setResearch(res.data);
        } catch (err) {
            setError('Research not found');
        }
        setLoading(false);
    }, [id]);

    useEffect(() => {
        fetchResearch();
    }, [fetchResearch]);

    const handleRequestAccess = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.email.trim()) {
            toast.error('Please fill in your name and email');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API}/research-requests`, {
                research_id: research.id,
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim()
            });
            toast.success('Request submitted! You will receive access shortly.');
            setRequested(true);
        } catch (error) {
            toast.error('Failed to submit request. Please try again.');
        }
        setSubmitting(false);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Navigation />
                <main className="py-16 sm:py-24">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="animate-pulse">
                            <div className="h-8 w-3/4 bg-muted mb-4" />
                            <div className="h-4 w-1/4 bg-muted mb-8" />
                            <div className="space-y-3">
                                <div className="h-4 w-full bg-muted" />
                                <div className="h-4 w-full bg-muted" />
                                <div className="h-4 w-2/3 bg-muted" />
                            </div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (error || !research) {
        return (
            <div className="min-h-screen bg-background">
                <Navigation />
                <main className="py-16 sm:py-24">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 
                            className="text-2xl sm:text-3xl mb-4"
                            style={{ fontFamily: "'Silkscreen', cursive" }}
                        >
                            Research Not Found
                        </h1>
                        <p className="text-muted-foreground mb-8">
                            The research you're looking for doesn't exist or has been removed.
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-primary hover:underline"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </Link>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="research-page">
            <Toaster position="top-right" />
            <Helmet>
                <title>{research?.title ? `${research.title} | Research | Shreyan Sahukar` : 'Research | Shreyan Sahukar'}</title>
                {research?.excerpt && <meta name="description" content={research.excerpt.slice(0, 160)} />}
                {research?.title && <meta property="og:title" content={`${research.title} | Shreyan Sahukar`} />}
                {research?.excerpt && <meta property="og:description" content={research.excerpt.slice(0, 160)} />}
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`https://shreyansahukar.com/research/${id}`} />
                <meta name="twitter:card" content="summary" />
                {research?.title && <meta name="twitter:title" content={research.title} />}
                <link rel="canonical" href={`https://shreyansahukar.com/research/${id}`} />
            </Helmet>
            <Navigation />
            
            <main className="py-16 sm:py-24">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back link */}
                    <Link
                        to="/#research"
                        className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8 text-sm uppercase tracking-widest"
                        data-testid="back-to-research"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Research
                    </Link>

                    {/* Header */}
                    <header className="mb-8">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="w-12 h-12 bg-primary flex items-center justify-center flex-shrink-0">
                                <FileText className="h-6 w-6 text-primary-foreground" />
                            </div>
                            <h1 
                                className="text-2xl sm:text-3xl lg:text-4xl tracking-tight uppercase"
                                style={{ fontFamily: "'Silkscreen', cursive" }}
                                data-testid="research-title"
                            >
                                {research.title}
                            </h1>
                        </div>
                    </header>

                    {/* Request Access Form */}
                    <div className="retro-card p-6 mb-8 border-primary" data-testid="research-request-form">
                        <div className="flex items-center gap-2 mb-4">
                            <Lock className="h-5 w-5 text-primary" />
                            <span 
                                className="text-lg uppercase tracking-widest font-bold"
                                style={{ fontFamily: "'Silkscreen', cursive" }}
                            >
                                Request Full Access
                            </span>
                        </div>

                        {requested ? (
                            <div className="p-4 bg-green-500/10 border-2 border-green-500">
                                <p className="text-green-600 dark:text-green-400 flex items-center gap-2">
                                    <span className="text-xl">✓</span>
                                    Access requested! You will receive the full document shortly.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleRequestAccess} className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest flex items-center gap-1 mb-1">
                                            <User className="h-3 w-3" /> Name *
                                        </Label>
                                        <Input 
                                            className="rounded-none border-2" 
                                            value={formData.name}
                                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                                            placeholder="Your name"
                                            required
                                            data-testid="research-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest flex items-center gap-1 mb-1">
                                            <Mail className="h-3 w-3" /> Email *
                                        </Label>
                                        <Input 
                                            type="email"
                                            className="rounded-none border-2" 
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            placeholder="your@email.com"
                                            required
                                            data-testid="research-email-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest flex items-center gap-1 mb-1">
                                        <Phone className="h-3 w-3" /> WhatsApp (Optional)
                                    </Label>
                                    <Input 
                                        className="rounded-none border-2" 
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        placeholder="+1 234 567 8900"
                                        data-testid="research-phone-input"
                                    />
                                </div>
                                <Button 
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-sm uppercase tracking-widest"
                                    data-testid="research-submit-btn"
                                >
                                    <Send className="h-4 w-4 mr-2" />
                                    {submitting ? 'Submitting...' : 'Request Full Document'}
                                </Button>
                            </form>
                        )}
                    </div>

                    {/* Research Excerpt */}
                    <div className="retro-card p-6 sm:p-8" data-testid="research-excerpt">
                        <div className="flex items-center gap-2 mb-4 pb-4 border-b-2 border-dashed border-border">
                            <span 
                                className="text-sm uppercase tracking-widest text-muted-foreground"
                                style={{ fontFamily: "'Silkscreen', cursive" }}
                            >
                                Preview Excerpt
                            </span>
                        </div>
                        
                        <div 
                            className="prose prose-lg dark:prose-invert max-w-none"
                            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                        >
                            {research.excerpt ? (
                                <p className="whitespace-pre-wrap leading-relaxed text-foreground">
                                    {research.excerpt}
                                </p>
                            ) : (
                                <p className="text-muted-foreground italic">
                                    No preview available. Request access to view the full document.
                                </p>
                            )}
                        </div>

                        {/* Locked content indicator */}
                        <div className="mt-8 p-6 bg-muted/30 border-2 border-dashed border-border text-center">
                            <Lock className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                            <p className="text-sm text-muted-foreground">
                                The full research document is gated. Fill out the request form above to receive access.
                            </p>
                        </div>
                    </div>

                    {/* Footer navigation */}
                    <div className="mt-12 pt-8 border-t-2 border-dashed border-border">
                        <Link
                            to="/#research"
                            className="inline-flex items-center gap-2 text-primary hover:underline text-sm uppercase tracking-widest"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            View All Research
                        </Link>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}
